"use client"

import { JSX, useEffect, useMemo, useRef, useState } from "react"
import { scaleBand, scaleLinear, max, format } from "d3"

/**
 * One x-axis category with a value per series.
 *
 * The `values` map is keyed by series key; any key listed in the chart's
 * `seriesKeys` that is missing here is treated as 0.
 */
export interface GroupedBarDatum {
  /** X-axis label for this category (one cluster of bars). */
  label: string
  /** value per series key. Missing keys are treated as 0. */
  values: Record<string, number>
}

/**
 * Props for the GroupedBarChart component.
 */
export interface GroupedBarChartProps {
  /** Ordered categories, left to right. Each renders one cluster of bars. */
  data: GroupedBarDatum[]
  /** Series keys, in left-to-right order within each cluster. */
  seriesKeys: string[]
  /**
   * Maps a series key to a stable colour.
   */
  colorFor: (seriesKey: string) => string
  /**
   * Formats an x-axis label for display. Defaults to the raw label.
   */
  formatXLabel?: (label: string) => string
  /**
   * Formats a series key for display in the tooltip. Defaults to the raw key.
   */
  formatSeriesLabel?: (seriesKey: string) => string
  /**
   * Width-to-height ratio of the chart (e.g. 2 renders twice as wide as tall).
  */
  aspectRatio?: number
  /**
   * Unit suffix appended after the value in the tooltip (e.g. "data points").
   * Omit for no unit. A leading space is added automatically.
   */
  valueUnit?: string
  /** Title drawn under the x axis (e.g. "Year"). Omit for no title. */
  xAxisLabel?: string
  /** Title drawn (rotated) beside the y axis (e.g. "Data points"). Omit for none. */
  yAxisLabel?: string
  /** Font size (px) for the x/y axis titles. Defaults to 12. */
  axisLabelFontSize?: number
  /**
   * Distance (viewBox units) the y-axis title sits inside the left margin edge.
   * Larger values move it rightward, closer to the bars. Defaults to 18.
   */
  yAxisLabelOffset?: number
  /** Rotation (degrees) for x-axis tick labels. 0 (default) keeps them flat. */
  xLabelAngle?: number
  /** Font size (px) for the x-axis tick labels. Defaults to 11. */
  xLabelFontSize?: number
  /** Font size (px) for the y-axis tick labels. Defaults to 11. */
  yLabelFontSize?: number
  /**
   * Caps the on-screen size of the tick labels and axis titles (in real CSS
   * px). Because the SVG scales to its container, the authored font sizes
   * otherwise grow on wide containers; this ceiling stops them ballooning.
   * Omit for no cap.
   */
  maxLabelFontSizePx?: number
  /**
   * Floors the on-screen size of the tick labels and axis titles (in real CSS
   * px), so they stay readable when the container is narrow and the SVG scales
   * the authored sizes down. Omit for no floor.
   */
  minLabelFontSizePx?: number
  /**
   * Per-side plot-area insets (viewBox units) that replace the computed margin
   * for that side. Omitted sides keep their default. Used to pull bars toward
   * the edges so they line up with an adjacent full-width control.
   */
  plotPadding?: Partial<{ top: number; right: number; bottom: number; left: number }>
  /**
   * Category labels to render as disabled: their bars are dimmed and ignore
   * hover, and their x-axis label is greyed. Use to show categories that fall
   * outside an active selection. Omit to enable every category.
   */
  disabledLabels?: ReadonlySet<string>
}

// Fixed inner margins around the plot area (space for axes and labels).
const BASE_MARGIN = { top: 8, right: 12, bottom: 28, left: 46 } as const

// Extra space reserved for an axis title, on top of the tick-label margins.
const X_AXIS_LABEL_SPACE = 22
const Y_AXIS_LABEL_SPACE = 20

// The chart is drawn in a fixed coordinate space and scaled to the container
// width by the SVG viewBox, so no runtime measurement is needed.
const VIEWBOX_WIDTH = 640

const DEFAULT_ASPECT_RATIO = VIEWBOX_WIDTH / 240

// Formats a y-axis tick value compactly (e.g. 12000 -> "12k").
const formatTick = format("~s")

const formatExact = format(",")

/**
 * A resolved y-axis tick: its value and its pixel position.
 */
interface Tick {
  value: number
  y: number
}

/**
 * A single rectangle to render (one series' bar within one category cluster).
 */
interface Bar {
  key: string
  seriesKey: string
  label: string
  value: number
  x: number
  y: number
  width: number
  height: number
  fill: string
}

/**
 * Tooltip state: which bar is hovered and where to position the tooltip.
 * Coordinates are relative to the chart's wrapping container (pixels).
 */
interface TooltipState {
  bar: Bar
  left: number
  top: number
}

/**
 * GroupedBarChart renders a responsive grouped (clustered) bar chart as plain
 * SVG. Within each x-axis category the series are drawn side by side rather
 * than stacked.
 *
 * D3 is used only for the maths (band/linear scales, tick/label formatting);
 * React renders the resulting `<rect>`/`<text>` elements. Hovering a bar shows
 * a tooltip with the series label and the exact value.
 *
 * @param props - See GroupedBarChartProps.
 * @returns The chart as a responsive, accessible SVG with a hover tooltip.
 */
export default function GroupedBarChart({
  data,
  seriesKeys,
  colorFor,
  formatXLabel,
  formatSeriesLabel,
  aspectRatio = DEFAULT_ASPECT_RATIO,
  valueUnit,
  xAxisLabel,
  yAxisLabel,
  axisLabelFontSize = 12,
  yAxisLabelOffset = 18,
  xLabelAngle = 0,
  xLabelFontSize = 11,
  yLabelFontSize = 11,
  maxLabelFontSizePx,
  minLabelFontSizePx,
  plotPadding,
  disabledLabels,
}: GroupedBarChartProps): JSX.Element {
  // The currently hovered bar and where to draw its tooltip, or null.
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)

  // Rendered width of the chart container (px), tracked only to enforce the
  // on-screen font-size clamp. Null until first measured / when no clamp is set.
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)

  // Only measure when a clamp (min or max) is actually requested.
  const clampLabelFontSize: boolean =
    maxLabelFontSizePx != null || minLabelFontSizePx != null

  useEffect(() => {
    if (!clampLabelFontSize) {
      return
    }
    const element: HTMLDivElement | null = containerRef.current
    if (element == null) {
      return
    }
    const observer: ResizeObserver = new ResizeObserver((entries) => {
      const entry: ResizeObserverEntry | undefined = entries[0]
      if (entry != null) {
        // Round + only commit on change so the font-clamp recompute doesn't
        // retrigger the observer on sub-pixel jitter.
        const nextWidth: number = Math.round(entry.contentRect.width)
        setContainerWidth((prev) => (prev === nextWidth ? prev : nextWidth))
      }
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [clampLabelFontSize])

  const viewBoxHeight: number = VIEWBOX_WIDTH / aspectRatio

  /**
   * Clamp a label's authored (viewBox-unit) font size so its on-screen size
   * stays within [minLabelFontSizePx, maxLabelFontSizePx]. The SVG scales user
   * units by containerWidth / VIEWBOX_WIDTH, so a user-unit size U renders at
   * U * scale px; a px bound B therefore maps to the user-unit bound B / scale.
   *
   * @param authored - The requested font size in viewBox units.
   * @returns The font size to use, clamped where an on-screen bound would bind.
   */
  function capLabelFontSize(authored: number): number {
    if (containerWidth == null || containerWidth <= 0) {
      return authored
    }
    const scale: number = containerWidth / VIEWBOX_WIDTH
    let result: number = authored
    if (maxLabelFontSizePx != null) {
      result = Math.min(result, maxLabelFontSizePx / scale)
    }
    if (minLabelFontSizePx != null) {
      result = Math.max(result, minLabelFontSizePx / scale)
    }
    return result
  }

  const effectiveXLabelFontSize: number = capLabelFontSize(xLabelFontSize)
  const effectiveYLabelFontSize: number = capLabelFontSize(yLabelFontSize)
  const effectiveAxisLabelFontSize: number = capLabelFontSize(axisLabelFontSize)

  // Rotated tick labels extend further below the axis than horizontal ones, so
  // reserve extra bottom space proportional to the tilt.
  const rotatedLabelSpace: number =
    xLabelAngle > 0 ? Math.round(Math.sin((xLabelAngle * Math.PI) / 180) * 24) : 0

  // Grow the base margins to make room for any axis titles, then let any
  // per-side plotPadding override replace the computed inset for that side.
  const margin = useMemo(() => {
    const computed = {
      top: BASE_MARGIN.top,
      right: BASE_MARGIN.right,
      bottom:
        BASE_MARGIN.bottom +
        rotatedLabelSpace +
        (xAxisLabel ? X_AXIS_LABEL_SPACE : 0),
      left: BASE_MARGIN.left + (yAxisLabel ? Y_AXIS_LABEL_SPACE : 0),
    }
    return {
      top: plotPadding?.top ?? computed.top,
      right: plotPadding?.right ?? computed.right,
      bottom: plotPadding?.bottom ?? computed.bottom,
      left: plotPadding?.left ?? computed.left,
    }
  }, [xAxisLabel, yAxisLabel, rotatedLabelSpace, plotPadding])

  // All layout maths run once per data/series change. The output is a plain
  // list of rectangles, axis ticks, and x labels that React renders directly.
  const { bars, ticks, xLabels, innerHeight } = useMemo(() => {
    const innerWidth = VIEWBOX_WIDTH - margin.left - margin.right
    const plotHeight = viewBoxHeight - margin.top - margin.bottom

    // Outer X scale — one band per category (cluster).
    const xScale = scaleBand<string>()
      .domain(data.map((d) => d.label))
      .range([0, innerWidth])
      .padding(0.2)

    // Inner X scale — one sub-band per series, within a category's band.
    const groupScale = scaleBand<string>()
      .domain(seriesKeys)
      .range([0, xScale.bandwidth()])
      .padding(0.1)

    // Tallest single bar (not stacked total) sets the top of the y axis. A zero
    // max falls back to 1 so the axis and scale stay valid.
    const maxValue =
      max(data, (d) => max(seriesKeys, (key) => d.values[key] ?? 0) ?? 0) ?? 0

    const yScale = scaleLinear()
      .domain([0, maxValue > 0 ? maxValue : 1])
      .nice()
      .range([plotHeight, 0])

    // One rectangle per (category, series) pair.
    const rects: Bar[] = []
    for (const datum of data) {
      const groupX = xScale(datum.label)
      if (groupX === undefined) {
        continue
      }
      for (const seriesKey of seriesKeys) {
        const innerX = groupScale(seriesKey)
        if (innerX === undefined) {
          continue
        }
        const value = datum.values[seriesKey] ?? 0
        rects.push({
          key: `${seriesKey}-${datum.label}`,
          seriesKey,
          label: datum.label,
          value,
          x: groupX + innerX,
          y: yScale(value),
          width: groupScale.bandwidth(),
          height: Math.max(0, plotHeight - yScale(value)),
          fill: colorFor(seriesKey),
        })
      }
    }

    const resolvedTicks: Tick[] = yScale
      .ticks(4)
      .map((value) => ({ value, y: yScale(value) }))

    const resolvedXLabels = data.map((d) => ({
      label: d.label,
      // Centre the label under its cluster.
      x: (xScale(d.label) ?? 0) + xScale.bandwidth() / 2,
    }))

    return {
      bars: rects,
      ticks: resolvedTicks,
      xLabels: resolvedXLabels,
      innerHeight: plotHeight,
    }
  }, [data, seriesKeys, colorFor, viewBoxHeight, margin])

  /**
   * Update the tooltip position from a pointer event over a bar.
   *
   * Positions the tooltip relative to the wrapping container using the mouse
   * coordinates within it, so it follows the cursor regardless of the SVG's
   * responsive scaling.
   *
   * @param event - The React pointer/mouse event on the bar rect.
   * @param bar - The hovered bar.
   */
  function handleBarHover(
    event: React.MouseEvent<SVGRectElement>,
    bar: Bar
  ): void {
    // currentTarget's owner container is the relative wrapper (the <div>).
    const container = event.currentTarget.ownerSVGElement?.parentElement
    if (!container) {
      return
    }
    const bounds = container.getBoundingClientRect()
    setTooltip({
      bar,
      left: event.clientX - bounds.left,
      top: event.clientY - bounds.top,
    })
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${viewBoxHeight}`}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label="Grouped bar chart"
        className="block w-full text-muted-foreground"
        style={{ aspectRatio: `${aspectRatio}` }}
      >
        {/* Plot area, offset by the margins. */}
        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* Y-axis gridlines + tick labels. */}
          {ticks.map((tick) => (
            <g key={tick.value}>
              <line
                x1={0}
                x2={VIEWBOX_WIDTH - margin.left - margin.right}
                y1={tick.y}
                y2={tick.y}
                stroke="currentColor"
                strokeOpacity={0.15}
              />
              <text
                x={-8}
                y={tick.y}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={effectiveYLabelFontSize}
                fill="currentColor"
              >
                {formatTick(tick.value)}
              </text>
            </g>
          ))}

          {/* Grouped bars. Zero-value bars are skipped so they neither render
              nor capture hover. Bars in a disabled category are dimmed and
              ignore hover. */}
          {bars.map((bar) => {
            if (bar.value <= 0) {
              return null
            }
            const isDisabled: boolean = disabledLabels?.has(bar.label) ?? false
            return (
              <rect
                key={bar.key}
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill={bar.fill}
                opacity={isDisabled ? 0.25 : 1}
                onMouseMove={
                  isDisabled ? undefined : (event) => handleBarHover(event, bar)
                }
                onMouseLeave={isDisabled ? undefined : () => setTooltip(null)}
              />
            )
          })}

          {/* X-axis labels, centred under each cluster when horizontal, or
              tilted and end-anchored at the cluster centre when rotated. */}
          {xLabels.map((item) => {
            const isDisabled: boolean = disabledLabels?.has(item.label) ?? false
            const labelOpacity: number = isDisabled ? 0.35 : 1
            return xLabelAngle > 0 ? (
              <text
                key={item.label}
                x={item.x}
                y={innerHeight + 12}
                textAnchor="end"
                transform={`rotate(${-xLabelAngle},${item.x},${innerHeight + 12})`}
                fontSize={effectiveXLabelFontSize}
                fill="currentColor"
                opacity={labelOpacity}
              >
                {formatXLabel ? formatXLabel(item.label) : item.label}
              </text>
            ) : (
              <text
                key={item.label}
                x={item.x}
                y={innerHeight + 18}
                textAnchor="middle"
                fontSize={effectiveXLabelFontSize}
                fill="currentColor"
                opacity={labelOpacity}
              >
                {formatXLabel ? formatXLabel(item.label) : item.label}
              </text>
            )
          })}

          {/* X-axis title, centred below the tick labels (pushed down further
              when the tick labels are rotated). */}
          {xAxisLabel && (
            <text
              x={(VIEWBOX_WIDTH - margin.left - margin.right) / 2}
              y={innerHeight + X_AXIS_LABEL_SPACE + 20 + rotatedLabelSpace}
              textAnchor="middle"
              fontSize={effectiveAxisLabelFontSize}
              fontWeight={500}
              fill="currentColor"
            >
              {xAxisLabel}
            </text>
          )}

          {/* Y-axis title, rotated upright to the left of the tick labels. */}
          {yAxisLabel && (
            <text
              transform={`translate(${-margin.left + yAxisLabelOffset},${innerHeight / 2}) rotate(-90)`}
              textAnchor="middle"
              fontSize={effectiveAxisLabelFontSize}
              fontWeight={500}
              fill="currentColor"
            >
              {yAxisLabel}
            </text>
          )}
        </g>
      </svg>

      {/* Hover tooltip — an absolutely-positioned overlay following the cursor. */}
      {tooltip && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-md border bg-popover px-2 py-1 text-xs shadow-md"
          style={{ left: tooltip.left, top: tooltip.top - 8 }}
          role="tooltip"
        >
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block size-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: tooltip.bar.fill }}
              aria-hidden="true"
            />
            <span className="font-medium">
              {formatSeriesLabel
                ? formatSeriesLabel(tooltip.bar.seriesKey)
                : tooltip.bar.seriesKey}
            </span>
          </div>
          <div className="mt-0.5 text-muted-foreground">
            {formatXLabel ? formatXLabel(tooltip.bar.label) : tooltip.bar.label}
            {": "}
            {formatExact(tooltip.bar.value)}
            {valueUnit ? ` ${valueUnit}` : ""}
          </div>
        </div>
      )}
    </div>
  )
}
